<script setup>
import { ref, computed, onMounted } from 'vue'
import ModalBox from './ModalBox.vue'
import SubscriptionService from '@/services/SubscriptionService'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormRadio from '@/components/Atoms/FormRadio.vue'
import FormText from '@/components/Atoms/FormText.vue'
import { useLegacyModalBridge } from '@/composables/useLegacyModalBridge'

const props = defineProps({
	modalId: { type: String, default: 'modal-unsubscribe-package' },
	modalSize: { type: String, default: 'md' },
	message: { type: String, default: '' },
	okText: { type: String, default: 'Ok' },
	cancelText: { type: String, default: 'Cancel' },
})

const emit = defineEmits(['modal-confirm'])

const cancellationReasons = ref([])
const comment = ref('')
const selected = ref('')
const okLabel = ref('Ok')
const cancelLabel = ref('Cancel')

const { isVisible, hide } = useLegacyModalBridge(() => props.modalId || 'modal-unsubscribe-package')

const state = computed(() => selected.value !== '')
const stateComment = computed(() => comment.value !== '')

onMounted(async () => {
	cancellationReasons.value = await SubscriptionService.getCancellationReasons()
	okLabel.value = props.okText || 'Ok'
	cancelLabel.value = props.cancelText || 'Cancel'
})

const dismiss = () => {
	hide()
}

const onConfirm = () => {
	if ((cancellationReasons.value.length > 0 && selected.value === '') || comment.value === '') {
		return
	}
	emit('modal-confirm', { reason: selected.value, comment: comment.value })
	dismiss()
}
</script>

<template>
	<ModalBox
		v-model="isVisible"
		:size="modalSize"
		hide-close
		header-class="!hidden"
		wrapper-classes="lg:h-auto lg:max-h-none"
		:close-on-backdrop="false"
		@close="dismiss"
	>
		<div class="px-6 pt-6">
			<p class="text-dark-8 mb-4">
				We always strive to improve our service. Please tell us why you decided to cancel:
			</p>
			<div v-if="cancellationReasons.length > 0" class="mb-4">
				<p class="text-dark-7 text-s-m mb-2">Cancellation Reasons</p>
				<div v-for="(reason, index) in cancellationReasons" :key="index" class="mb-1">
					<FormRadio v-model="selected" name="cancel-reason" :inputValue="reason" :label="reason" class="text-s-s" />
				</div>
				<p v-if="!state" class="text-danger text-sm mt-1">Please select one</p>
			</div>
			<FormText
				label="Comments"
				:isError="!stateComment"
				id="textarea"
				v-model="comment"
				placeholder=""
				rows="5"
				max-rows="10"
				class="mb-3"
			/>
			<span v-if="message" class="text-dark-7">{{ message }}</span>
		</div>
		<template #footer>
			<div class="flex justify-end gap-2 p-6 border-t border-surface-3">
				<FormButton type="secondary" size="md" @click="dismiss">
					{{ cancelLabel }}
				</FormButton>
				<FormButton type="main" size="md" @click="onConfirm">
					{{ okLabel }}
				</FormButton>
			</div>
		</template>
	</ModalBox>
</template>
